import React from 'react'
import Link from 'next/link'
import { MediaBlock } from '@/blocks/MediaBlock/Component'
import { BannerBlock } from '@/blocks/Banner/Component'
import { CallToActionBlock } from '@/blocks/CallToAction/Component'
import { cn } from '@/utilities/ui'

// ── Text format bitmasks (from Lexical constants) ────────────────────
const IS_BOLD = 1
const IS_ITALIC = 1 << 1
const IS_STRIKETHROUGH = 1 << 2
const IS_UNDERLINE = 1 << 3
const IS_CODE = 1 << 4
const IS_SUBSCRIPT = 1 << 5
const IS_SUPERSCRIPT = 1 << 6

// ── Serialized node types ────────────────────────────────────────────
type SerializedNode = {
  type: string
  children?: SerializedNode[]
  text?: string
  format?: number | string
  tag?: string
  listType?: string
  value?: any
  checked?: boolean
  fields?: any
  indent?: number
  headerState?: number
  colSpan?: number
  rowSpan?: number
  backgroundColor?: string
  [key: string]: any
}

type EditorState = {
  root?: {
    children?: SerializedNode[]
  }
}

// ── hasText check ────────────────────────────────────────────────────
function hasText(data: EditorState): boolean {
  const children = data?.root?.children
  if (!children?.length) return false
  if (children.length === 1 && children[0].type === 'paragraph') {
    const p = children[0]
    if (!p.children?.length) return false
    if (p.children.length === 1 && p.children[0].type === 'text' && !p.children[0].text?.length) return false
  }
  return true
}

// ── Render a single node ─────────────────────────────────────────────
function renderNode(node: SerializedNode, index: number, parent?: SerializedNode): React.ReactNode {
  switch (node.type) {
    case 'text':
      return renderText(node, index)

    case 'paragraph': {
      const children = renderChildren(node)
      if (!children?.length) return <p key={index}><br /></p>
      return <p key={index}>{children}</p>
    }

    case 'heading': {
      const Tag = (node.tag || 'h2') as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
      return <Tag key={index}>{renderChildren(node)}</Tag>
    }

    case 'quote':
      return <blockquote key={index}>{renderChildren(node)}</blockquote>

    case 'list': {
      const Tag = node.tag === 'ol' ? 'ol' : 'ul'
      return <Tag key={index} className={`list-${node.listType}`}>{renderChildren(node)}</Tag>
    }

    case 'listitem': {
      const hasSubList = node.children?.some((c) => c.type === 'list')
      if (parent?.listType === 'check') {
        return (
          <li
            key={index}
            className={`list-item-checkbox${node.checked ? ' list-item-checkbox-checked' : ' list-item-checkbox-unchecked'}${hasSubList ? ' nestedListItem' : ''}`}
            role="checkbox"
            aria-checked={node.checked ? 'true' : 'false'}
            style={{ listStyleType: 'none' }}
            tabIndex={-1}
            value={node.value}
          >
            {renderChildren(node)}
          </li>
        )
      }
      return (
        <li
          key={index}
          className={hasSubList ? 'nestedListItem' : undefined}
          style={hasSubList ? { listStyleType: 'none' } : undefined}
          value={node.value}
        >
          {renderChildren(node)}
        </li>
      )
    }

    case 'link':
    case 'autolink': {
      const { fields } = node
      const rel = fields?.newTab ? 'noopener noreferrer' : undefined
      const target = fields?.newTab ? '_blank' : undefined
      let href = fields?.url ?? ''
      if (fields?.linkType === 'internal' && fields?.doc) {
        const doc = typeof fields.doc.value === 'object' ? fields.doc.value : null
        href = doc?.slug ? `/${doc.slug}` : '#'
      }
      return (
        <a key={index} href={href} rel={rel} target={target}>
          {renderChildren(node)}
        </a>
      )
    }

    case 'linebreak':
      return <br key={index} />

    case 'horizontalrule':
      return <hr key={index} />

    case 'tab':
      return '\t'

    case 'upload':
      return renderUpload(node, index)

    case 'table':
      return (
        <div key={index} className="lexical-table-container">
          <table className="lexical-table" style={{ borderCollapse: 'collapse' }}>
            <tbody>{renderChildren(node)}</tbody>
          </table>
        </div>
      )

    case 'tablerow':
      return <tr key={index} className="lexical-table-row">{renderChildren(node)}</tr>

    case 'tablecell': {
      const Tag = (node.headerState ?? 0) > 0 ? 'th' : 'td'
      return (
        <Tag
          key={index}
          className={`lexical-table-cell lexical-table-cell-header-${node.headerState ?? 0}`}
          colSpan={node.colSpan && node.colSpan > 1 ? node.colSpan : undefined}
          rowSpan={node.rowSpan && node.rowSpan > 1 ? node.rowSpan : undefined}
          style={{ backgroundColor: node.backgroundColor || undefined, border: '1px solid #ccc', padding: '8px' }}
        >
          {renderChildren(node)}
        </Tag>
      )
    }

    // ── Block nodes (CMS blocks embedded in richtext) ──
    case 'block': {
      const blockType = node.fields?.blockType
      if (blockType === 'banner') return <BannerBlock key={index} className="col-start-2 mb-4" {...node.fields} />
      if (blockType === 'mediaBlock') return <MediaBlock key={index} className="col-start-1 col-span-3" imgClassName="m-0" {...node.fields} captionClassName="mx-auto max-w-[48rem]" enableGutter={false} disableInnerContainer />
      if (blockType === 'cta') return <CallToActionBlock key={index} {...node.fields} />
      return null
    }

    default:
      return null
  }
}

// ── Render text with format bitmask ──────────────────────────────────
function renderText(node: SerializedNode, index: number): React.ReactNode {
  const format = typeof node.format === 'number' ? node.format : 0
  let result: React.ReactNode = node.text || ''

  if (format & IS_BOLD) result = <strong>{result}</strong>
  if (format & IS_ITALIC) result = <em>{result}</em>
  if (format & IS_STRIKETHROUGH) result = <span style={{ textDecoration: 'line-through' }}>{result}</span>
  if (format & IS_UNDERLINE) result = <span style={{ textDecoration: 'underline' }}>{result}</span>
  if (format & IS_CODE) result = <code>{result}</code>
  if (format & IS_SUBSCRIPT) result = <sub>{result}</sub>
  if (format & IS_SUPERSCRIPT) result = <sup>{result}</sup>

  return <React.Fragment key={index}>{result}</React.Fragment>
}

// ── Render upload node ───────────────────────────────────────────────
function renderUpload(node: SerializedNode, index: number): React.ReactNode {
  const doc = typeof node.value === 'object' ? node.value : null
  if (!doc) return null

  const alt = node.fields?.alt || doc.alt || ''
  const url = doc.url

  if (!doc.mimeType?.startsWith('image')) {
    return <a key={index} href={url} rel="noopener noreferrer">{doc.filename}</a>
  }

  if (!doc.sizes || !Object.keys(doc.sizes).length) {
    return <img key={index} alt={alt} src={url} width={doc.width} height={doc.height} />
  }

  return (
    <picture key={index}>
      {Object.entries(doc.sizes).map(([sizeName, imageSize]: [string, any]) => {
        if (!imageSize?.url || !imageSize?.width || !imageSize?.mimeType) return null
        return <source key={sizeName} media={`(max-width: ${imageSize.width}px)`} srcSet={imageSize.url} type={imageSize.mimeType} />
      })}
      <img alt={alt} src={url} width={doc.width} height={doc.height} />
    </picture>
  )
}

// ── Render children with alignment/indent support ────────────────────
function renderChildren(node: SerializedNode): React.ReactNode[] {
  if (!node.children?.length) return []
  return node.children.map((child, i) => {
    const rendered = renderNode(child, i, node)
    if (!React.isValidElement(rendered)) return rendered

    const style: React.CSSProperties = {}
    if (child.format && typeof child.format === 'string') {
      const alignMap: Record<string, string> = { center: 'center', end: 'right', justify: 'justify', right: 'right', start: 'left' }
      if (alignMap[child.format]) style.textAlign = alignMap[child.format] as any
    }
    if (child.indent && child.type !== 'listitem') {
      style.paddingInlineStart = `${Number(child.indent) * 40}px`
    }

    if (style.textAlign || style.paddingInlineStart) {
      return React.cloneElement(rendered as React.ReactElement<any>, { key: i, style: { ...style, ...(rendered.props as any)?.style } })
    }
    return rendered
  })
}

// ── Main component ───────────────────────────────────────────────────
type Props = {
  data: EditorState
  enableGutter?: boolean
  enableProse?: boolean
} & React.HTMLAttributes<HTMLDivElement>

export default function RichText(props: Props) {
  const { className, enableProse = true, enableGutter = true, data, ...rest } = props

  if (!hasText(data)) return null

  const content = renderChildren({ children: data.root?.children } as SerializedNode)

  return (
    <div
      className={cn(
        'payload-richtext',
        {
          container: enableGutter,
          'max-w-none': !enableGutter,
          'mx-auto prose md:prose-md': enableProse,
        },
        className,
      )}
      {...rest}
    >
      {content}
    </div>
  )
}
